import { Component, OnInit, Inject } from '@angular/core';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { DishService } from 'src/services/data/dish/dish.service';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { Router } from '@angular/router';
import { Restaurant } from 'src/services/classes/models/restaurant';
import { RestaurantService } from 'src/services/data/restaurant/restaurant.service';

interface PaymentMethod {
  id: number;
  icon: string;
  label: string;
  description: string;
  type?: string;
  show?: boolean;
}

@Component({
  selector: 'app-checkout-options',
  templateUrl: './checkout-options.component.html',
  styleUrls: ['./checkout-options.component.scss'],
})
export class CheckoutOptionsComponent implements OnInit {
  selectedMethodId = null;
  selectedMethod: PaymentMethod = null;
  btnText = 'Proceed to payment';
  restaurantInfo: Restaurant = null;
  selectedPaymentPlan: 'full' | 'split' = 'full';
  totalToPay = 0;
  splitSelectedMethodIds: number[] = [];
  firstSplitAmount = 0;
  paymentMethods: PaymentMethod[] = [
    {
      icon: '/assets/icons/cash-payment.svg',
      id: 4,
      label: 'Pay with cash',
      description: 'A waiter will come to collect your payment.',
      type: 'cash',
    },
    {
      icon: '/assets/icons/pos-payment.svg',
      id: 5,
      label: 'Pay via POS',
      description: 'Pay offline with your card via POS.',
      type: 'pos',
    },
    {
      icon: '/assets/icons/online-payment.svg',
      id: 6,
      label: 'Pay online',
      description: 'Pay online with your card',
      type: 'online',
    },
    {
      icon: '/assets/icons/online-payment.svg',
      id: 7,
      label: 'Pay via Transfer',
      description: 'Pay offline via transfer',
      type: 'transfer',
    },
  ];

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private restaurantService: RestaurantService,
    private bottomSheetRef: MatBottomSheetRef<CheckoutOptionsComponent>,
    private dishService: DishService,
    private notifyService: NotificationsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.restaurantInfo = this.restaurantService.fetchRestaurantInformation();
    this.totalToPay =
      Number(this.data?.totalToPay) || Number(localStorage.getItem('cartTotal')) || 0;
    if (this.restaurantInfo.settings) {
      this.paymentMethods.forEach((paymentMethod: PaymentMethod) => {
        if (Object.hasOwn(this.restaurantInfo.settings, paymentMethod.type)) {
          paymentMethod.show = this.restaurantInfo.settings[paymentMethod.type];
        }
      });
    }
  }

  closeBottomSheet() {
    this.bottomSheetRef.dismiss('closed');
  }

  selectPaymentPlan(plan: 'full' | 'split') {
    this.selectedPaymentPlan = plan;
    if (plan === 'full') {
      this.splitSelectedMethodIds = [];
      this.firstSplitAmount = 0;
      return;
    }
    this.selectedMethodId = null;
    this.selectedMethod = null;
    this.btnText = 'Proceed to payment';
  }

  selectMethod(method: PaymentMethod) {
    if (this.selectedPaymentPlan === 'split') {
      this.toggleSplitMethod(method);
      return;
    }

    this.selectedMethodId = method.id;
    this.selectedMethod = method;
    if (method.id !== 6) {
      this.btnText = 'Notify waiter';
    } else {
      this.btnText = 'Proceed to payment';
    }
  }

  toggleSplitMethod(method: PaymentMethod) {
    const existingIdx = this.splitSelectedMethodIds.indexOf(method.id);
    if (existingIdx >= 0) {
      this.splitSelectedMethodIds.splice(existingIdx, 1);
      if (this.splitSelectedMethodIds.length < 2) {
        this.firstSplitAmount = 0;
      }
      return;
    }

    if (this.splitSelectedMethodIds.length >= 2) {
      this.notifyService.publishMessages(
        'You can only select two payment channels for split payment.',
        'info',
        1
      );
      return;
    }

    this.splitSelectedMethodIds.push(method.id);
  }

  isMethodSelected(method: PaymentMethod): boolean {
    if (this.selectedPaymentPlan === 'split') {
      return this.splitSelectedMethodIds.includes(method.id);
    }
    return this.selectedMethodId === method.id;
  }

  isFirstSplitMethod(method: PaymentMethod): boolean {
    return this.splitSelectedMethodIds[0] === method.id;
  }

  isSecondSplitMethod(method: PaymentMethod): boolean {
    return this.splitSelectedMethodIds[1] === method.id;
  }

  get secondSplitAmount(): number {
    const remaining = this.totalToPay - this.firstSplitAmount;
    return remaining > 0 ? remaining : 0;
  }

  updateFirstSplitAmount(rawValue: string) {
    const parsed = Number(rawValue);
    if (isNaN(parsed) || parsed < 0) {
      this.firstSplitAmount = 0;
      return;
    }
    this.firstSplitAmount = parsed > this.totalToPay ? this.totalToPay : parsed;
  }

  get canProceed(): boolean {
    if (this.selectedPaymentPlan === 'split') {
      return (
        this.splitSelectedMethodIds.length === 2 &&
        this.firstSplitAmount > 0 &&
        this.secondSplitAmount > 0
      );
    }
    return this.selectedMethodId !== null;
  }

  private getMethodById(methodId: number): PaymentMethod | null {
    return this.paymentMethods.find((method) => method.id === methodId) || null;
  }

  private buildPaymentPayload() {
    if (this.selectedPaymentPlan === 'split') {
      const firstMethod = this.getMethodById(this.splitSelectedMethodIds[0]);
      const secondMethod = this.getMethodById(this.splitSelectedMethodIds[1]);
      return {
        notificationType: 'split-payment',
        paymentType: 'split',
        splits: [
          {
            method: firstMethod?.type,
            amount: this.firstSplitAmount,
          },
          {
            method: secondMethod?.type,
            amount: this.secondSplitAmount,
          },
        ],
      };
    }

    return {
      notificationType: 'split-payment',
      paymentType: 'full',
      mode: this.selectedMethod?.type,
    };
  }

  proceedToCheckout() {
    const paymentPayload = this.buildPaymentPayload();
    localStorage.setItem('payment-notification-payload', JSON.stringify(paymentPayload));

    this.closeBottomSheet();

    if (this.selectedPaymentPlan === 'split') {
      this.router.navigateByUrl('/awaiting-payment');
      return;
    }

    const route =
      this.selectedMethodId === 6 ? 'collect-email' : 'awaiting-payment';

    route === 'awaiting-payment'
      ? this.router.navigate(['/', route], {
          queryParams: { mode: this.selectedMethod.type },
        })
      : this.router.navigateByUrl(`/${route}`);
  }
}
